// frontend/src/pages/OvertimeMonitoring/components/EmployeeOvertimeTable.js
import React, { useState, useEffect, useMemo } from 'react';
import { getClassificationSolidColor } from '../../../utils/classificationConfig';
import { formatDistanceToNow } from 'date-fns';
import { 
  Activity,
  AlertTriangle, 
  ChevronUp, 
  ChevronDown,
  Eye,
  TrendingUp,
  CalendarClock,
  Zap,
  CalendarDays,
  Building2,
  Wrench,
  RefreshCw,
  Download,
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { DepartmentTradeChips } from '../../../components/UI/DepartmentTradeChips';
import { Pagination } from '../../../components/UI/Pagination';
import { FilterButton } from '../../../components/UI/FilterButton';
import PillSelect from '../../../components/UI/PillSelect';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { OvertimeProgressBar } from '../../../components/UI/OvertimeProgressBar';
import OvertimeExportModal from './OvertimeExportModal';

/**
 * CacheStatusIndicator — small muted line showing when the OT
 * monitoring snapshot was last warmed by the background Celery beat
 * task. Pure presentational: pulls `last_updated` / `cache_hit` /
 * `refresh_interval_seconds` from the `cacheStatus` object the hook
 * polls on a 60-second interval.
 *
 * Hidden entirely for historical months (no overlay → no marker).
 */
const CacheStatusIndicator = ({ cacheStatus, isCurrentMonth, className = '' }) => {
  if (!isCurrentMonth || !cacheStatus) return null;
  const { last_updated: lastUpdated, cache_hit: cacheHit, refresh_interval_seconds: refreshInterval } = cacheStatus;
  if (!lastUpdated) {
    return (
      <div
        className={`flex items-center gap-1.5 text-xs text-gray-500 ${className}`}
        role="status"
        aria-live="polite"
      >
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span>Refreshing…</span>
      </div>
    );
  }
  let relative;
  try {
    relative = formatDistanceToNow(new Date(lastUpdated), { addSuffix: true });
  } catch {
    relative = 'just now';
  }
  const refreshLabel = refreshInterval
    ? `refreshes every ${Math.round(refreshInterval / 60)} min`
    : 'auto-refreshing';
  return (
    <div
      className={`flex items-center gap-1.5 text-xs text-gray-500 ${className}`}
      role="status"
      aria-live="polite"
      title={`Last warmed: ${new Date(lastUpdated).toLocaleString('en-SG')}${cacheHit ? '' : ' (cache miss — current data may be stale)'}`}
    >
      <RefreshCw className={`w-3 h-3 ${cacheHit ? '' : 'animate-spin text-amber-500'}`} />
      <span>
        Data as of {relative}
        <span className="mx-1 text-gray-400">·</span>
        {refreshLabel}
      </span>
    </div>
  );
};

/**
 * Helper function to convert decimal hours to "hr min" format
 * Example: 31.36 → "31h 22min"
 */
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0h';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}min`;
};

/**
 * List/table chrome label for the unified OT status.
 * `projected_exceed` is "Projected Exceed" in statusConfig — too wide for
 * a list accessory. The detail modal already shortens it to "Will Exceed".
 */
const overtimeStatusBadgeLabel = (status) => (
  status === 'projected_exceed' ? 'Will Exceed' : undefined
);

// Delegates to classificationConfig — the single source of truth for classification colours.
const getAvatarColor = (classification) => getClassificationSolidColor(classification);

/**
 * EmployeeOvertimeTable - Sortable, filterable table of workmen with OT status
 * 
 * Features:
 * - Department, status, and discrepancy filters
 * - Sortable columns
 * - Progress bars showing OT hours vs 72h limit
 * - Unified "Status" column (merges old Risk Level + Forecast)
 * - Client-side pagination (PAGE_SIZE=25, matches RestDayMonitoring)
 */
const PAGE_SIZE = 25;

const EmployeeOvertimeTable = ({ 
  employees, 
  loading, 
  filters, 
  onFilterChange,
  onViewEmployee,
  departments = [],
  isCurrentMonth = true,
  cacheStatus = null,
  selectedMonth = '',
  monthOptions = [],
  fetchEmployeesForMonth,
}) => {
  const [sortField, setSortField] = useState('currentOtHours');
  const [sortDirection, setSortDirection] = useState('desc');
  const [localSearch, setLocalSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showExportModal, setShowExportModal] = useState(false);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort and filter employees
  const sortedEmployees = useMemo(() => {
    let filtered = [...employees];
    
    // Apply local search
    if (localSearch) {
      const search = localSearch.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.fullName?.toLowerCase().includes(search) ||
        emp.staffId?.toLowerCase().includes(search) ||
        emp.department?.toLowerCase().includes(search)
      );
    }
    
    // Sort
    const statusOrder = { exceeded: 6, critical: 5, projected_exceed: 4, at_risk: 3, watch: 2, on_track: 1, compliant: 0 };
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Use severity ordering for unified status
      if (sortField === 'unifiedStatus') {
        aVal = statusOrder[aVal] ?? 0;
        bVal = statusOrder[bVal] ?? 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [employees, localSearch, sortField, sortDirection]);

  // Get unique departments from employees
  const uniqueDepartments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [employees]);

  // Get unique trades from employees
  const uniqueTrades = useMemo(() => {
    const trades = new Set(employees.map(e => e.trade).filter(Boolean).filter(t => t !== 'No Trade'));
    return Array.from(trades).sort();
  }, [employees]);

  useEffect(() => {
    setCurrentPage(1);
  }, [localSearch, sortField, sortDirection, filters.riskLevel, filters.department, filters.hasDiscrepancy, filters.trade]);

  const totalPages = Math.ceil(sortedEmployees.length / PAGE_SIZE);
  const paginatedEmployees = useMemo(() =>
    sortedEmployees.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedEmployees, currentPage]
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1" /> : 
      <ChevronDown className="w-4 h-4 ml-1" />;
  };

  // Opening the export modal lets HR pick one OR several months and get a
  // single branded workbook. The month currently on screen is pre-selected
  // and its already-loaded roster (`sortedEmployees` — full filtered+sorted
  // set, not just the page slice) is handed to the modal so the common
  // single-month export is instant (no second snapshot build).
  const exportFilters = { ...filters, search: localSearch };
  const preloadedMonth = {
    yearMonth: selectedMonth,
    employees: sortedEmployees,
    isCurrentMonth,
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="h-10 bg-gray-200 rounded animate-pulse w-full max-w-md" />
        </div>
        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-4" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/6" />
                </div>
                <div className="h-6 bg-gray-200 rounded w-20 mx-4" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* Search */}
          <DebouncedSearchInput
            value={localSearch}
            onSearch={setLocalSearch}
            placeholder="Search employees..."
            theme="admin"
            className="max-w-xl"
          />

          {/* Right-aligned controls — DS sequence: Filter (3) → Export (4) */}
          <div className="ml-auto flex items-center gap-2">
            <CacheStatusIndicator
              cacheStatus={cacheStatus}
              isCurrentMonth={isCurrentMonth}
              className="hidden md:flex"
            />
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={[filters.department, filters.trade, filters.riskLevel, filters.hasDiscrepancy].filter(Boolean).length}
              onClick={() => setShowFilters(v => !v)}
            />
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              title="Export to Excel — select one or more months"
              aria-label="Export to Excel"
              className="icon-button-admin"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Cache status below search on mobile */}
        <div className="mt-2 md:hidden">
          <CacheStatusIndicator
            cacheStatus={cacheStatus}
            isCurrentMonth={isCurrentMonth}
          />
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Department Filter */}
              <div>
                <label className="form-label-admin">Department</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Departments' },
                    ...uniqueDepartments.map(dept => ({ value: dept, label: dept }))
                  ]}
                  value={filters.department || ''}
                  onChange={(val) => onFilterChange({ department: val })}
                  theme="admin"
                  icon={Building2}
                  iconColor="text-red-600"
                  className="w-full"
                />
              </div>

              {/* Trade Filter */}
              <div>
                <label className="form-label-admin">Trade</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Trades' },
                    ...uniqueTrades.map(trade => ({ value: trade, label: trade }))
                  ]}
                  value={filters.trade || ''}
                  onChange={(val) => onFilterChange({ trade: val })}
                  theme="admin"
                  icon={Wrench}
                  iconColor="text-red-600"
                  className="w-full"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="form-label-admin">Status</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'action_required', label: '⚠ Action Required' },
                    { value: 'watch_list', label: '👁 Watch List' },
                    { isGroupHeader: true, label: '──────────' },
                    { value: 'exceeded', label: 'Exceeded (>72h)' },
                    { value: 'critical', label: 'Critical (60-72h)' },
                    ...(isCurrentMonth ? [{ value: 'projected_exceed', label: 'Will Exceed (projected)' }] : []),
                    { value: 'at_risk', label: 'At Risk (48-60h)' },
                    ...(isCurrentMonth ? [{ value: 'watch', label: 'Watch (projected 60h+)' }] : []),
                    { value: 'compliant', label: 'OK (<48h)' },
                  ]}
                  value={filters.riskLevel || ''}
                  onChange={(val) => onFilterChange({ riskLevel: val })}
                  icon={Activity}
                  iconColor="text-red-600"
                  theme="admin"
                  className="w-full"
                />
              </div>

              {/* Discrepancy Filter - Only for current month */}
              {isCurrentMonth && (
                <div>
                  <label className="form-label-admin">Discrepancy</label>
                  <PillSelect
                    options={[
                      { value: '', label: 'All Workmen' },
                      { value: 'true', label: 'With Undeclared OT' },
                      { value: 'false', label: 'No Discrepancy' },
                    ]}
                    value={filters.hasDiscrepancy || ''}
                    onChange={(val) => onFilterChange({ hasDiscrepancy: val })}
                    theme="admin"
                    icon={AlertTriangle}
                    iconColor="text-red-600"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden divide-y divide-gray-100">
        {sortedEmployees.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            No employees found matching your criteria
          </div>
        ) : (
          paginatedEmployees.map(employee => {
            const status = employee.unifiedStatus || employee.riskLevel || 'compliant';
            const forecast = employee.forecast;
            const capEligibleHours = employee.capEligibleOtHours ?? employee.currentOtHours;
            const showForecastMeta = isCurrentMonth && forecast &&
              (status === 'projected_exceed' || status === 'watch');

            return (
              <button
                key={employee.staffId}
                onClick={() => onViewEmployee?.(employee)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
              >
                {/* Hours + status stack on the right; bar keeps a shared full-width track. */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 ${getAvatarColor(employee.employmentClassification)} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className="text-xs font-medium text-white">
                        {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{employee.fullName}</p>
                      <p className="text-xs text-gray-500">{employee.staffId}</p>
                      <div className="mt-0.5">
                        <DepartmentTradeChips
                          department={employee.department}
                          trade={employee.trade}
                          layout="inline"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">
                      {formatHoursMinutes(capEligibleHours)}
                    </p>
                    <div className="flex items-center gap-1">
                      <StatusBadge status={status} label={overtimeStatusBadgeLabel(status)} />
                      {employee.hasUndeclaredOt && (
                        <span className="text-orange-500">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2: full-width 72h track — same geometry every row */}
                <div className="mt-2 ml-[52px]">
                  <OvertimeProgressBar
                    hours={capEligibleHours}
                    ariaLabel={`${formatHoursMinutes(capEligibleHours)} counted toward the MOM 72-hour cap. Rest Day / PH regular blocks are excluded.`}
                  />
                  {showForecastMeta && (
                    <p className="mt-1 text-[10px] text-gray-400 leading-tight flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-0.5 text-red-500 font-semibold">
                        <TrendingUp className="w-3 h-3 flex-shrink-0" />
                        {formatHoursMinutes(forecast.projectedEomOt)}
                      </span>
                      {forecast.projectedBreachDate && (
                        <span className="inline-flex items-center gap-0.5 text-red-500 font-medium">
                          <CalendarClock className="w-3 h-3 flex-shrink-0" />
                          {new Date(forecast.projectedBreachDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-0.5">
                        <Zap className="w-3 h-3 flex-shrink-0" />
                        {employee.daysWithOt}d
                      </span>
                      <span className="inline-flex items-center gap-0.5">
                        <CalendarDays className="w-3 h-3 flex-shrink-0" />
                        {forecast.workingDaysRemaining}d
                      </span>
                    </p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('fullName')}
              >
                <div className="flex items-center">
                  Employee
                  <SortIcon field="fullName" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('department')}
              >
                <div className="flex items-center">
                  Department
                  <SortIcon field="department" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('currentOtHours')}
              >
                <div className="flex items-center">
                  72h Cap OT
                  <SortIcon field="currentOtHours" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('unifiedStatus')}
              >
                <div className="flex items-center">
                  Status
                  <SortIcon field="unifiedStatus" />
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedEmployees.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  No employees found matching your criteria
                </td>
              </tr>
            ) : (
              paginatedEmployees.map(employee => {
                const status = employee.unifiedStatus || employee.riskLevel || 'compliant';
                const forecast = employee.forecast;
                const capEligibleHours = employee.capEligibleOtHours ?? employee.currentOtHours;
                const showForecastMeta = isCurrentMonth && forecast && 
                  (status === 'projected_exceed' || status === 'watch');

                return (
                  <tr 
                    key={employee.staffId} 
                    className="table-row"
                  >
                    {/* Employee */}
                    <td className="px-6 py-3">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 ${getAvatarColor(employee.employmentClassification)} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                          <span className="text-xs font-medium text-white">
                            {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{employee.fullName}</p>
                          <p className="text-xs text-gray-500">{employee.staffId}</p>
                        </div>
                      </div>
                    </td>
                    
                    {/* Department + Trade — dense chips (dept primary, trade secondary) */}
                    <td className="px-6 py-3">
                      <DepartmentTradeChips
                        department={employee.department}
                        trade={employee.trade}
                      />
                    </td>
                    
                    {/* OT Hours with Progress Bar */}
                    <td className="px-6 py-3">
                      <OvertimeProgressBar
                        hours={capEligibleHours}
                        ariaLabel={`${formatHoursMinutes(capEligibleHours)} counted toward the MOM 72-hour cap. Rest Day / PH regular blocks are excluded.`}
                      />
                    </td>
                    
                    {/* Unified Status (merged Risk Level + Forecast) */}
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={status} label={overtimeStatusBadgeLabel(status)} />
                        {employee.hasUndeclaredOt && (
                          <span className="text-orange-500" title="Potential undeclared overtime">
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                      {showForecastMeta && (
                        <p className="mt-0.5 text-[10px] text-gray-400 leading-tight flex items-center gap-1.5">
                          <span
                            className="inline-flex items-center gap-0.5 text-red-500 font-semibold"
                            title="Projected total OT hours by end of month"
                          >
                            <TrendingUp className="w-3 h-3 flex-shrink-0" />
                            {formatHoursMinutes(forecast.projectedEomOt)}
                          </span>
                          {forecast.projectedBreachDate && (
                            <span
                              className="inline-flex items-center gap-0.5 text-red-500 font-medium"
                              title="Estimated date the 72-hour MOM limit will be breached"
                            >
                              <CalendarClock className="w-3 h-3 flex-shrink-0" />
                              {new Date(forecast.projectedBreachDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                          <span
                            className="inline-flex items-center gap-0.5"
                            title="Working days this month where OT was clocked"
                          >
                            <Zap className="w-3 h-3 flex-shrink-0" />
                            {employee.daysWithOt}d
                          </span>
                          <span
                            className="inline-flex items-center gap-0.5"
                            title="Working days remaining in the month"
                          >
                            <CalendarDays className="w-3 h-3 flex-shrink-0" />
                            {forecast.workingDaysRemaining}d
                          </span>
                        </p>
                      )}
                    </td>
                    
                    {/* Actions */}
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => onViewEmployee?.(employee)}
                        className="icon-button-admin"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalCount={sortedEmployees.length}
        pageSize={PAGE_SIZE}
        theme="admin"
        noun="workmen"
      />

      {/* Footer */}
      {sortedEmployees.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {isCurrentMonth 
                ? `${sortedEmployees.filter(e => e.hasUndeclaredOt).length} workmen with potential undeclared OT`
                : ''
              }
            </span>
            <span>
              Hours remaining avg: {
                sortedEmployees.length > 0 
                  ? formatHoursMinutes(sortedEmployees.reduce((sum, e) => sum + e.hoursRemaining, 0) / sortedEmployees.length)
                  : '0h'
              }
            </span>
          </div>
        </div>
      )}

      {/* Multi-month Excel export */}
      <OvertimeExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        monthOptions={monthOptions}
        defaultMonth={selectedMonth}
        scopeFilters={exportFilters}
        preloaded={preloadedMonth}
        fetchMonth={fetchEmployeesForMonth}
      />
    </div>
  );
};

export default EmployeeOvertimeTable;
